// src/hooks/useIntersection.ts
import { useRef, useCallback, useEffect } from 'react'

// Заменяем interface на type
type UseIntersectionOptions = IntersectionObserverInit

/**
 * Хук для отслеживания пересечения элемента с областью видимости.
 * Вызывает колбэк, когда элемент становится видимым.
 * @param onIntersect - Колбэк, вызываемый при пересечении элемента.
 * @param options - Опции для IntersectionObserver (threshold, root, rootMargin).
 * @returns callback ref, который нужно присвоить отслеживаемому элементу.
 */
export function useIntersection(
    onIntersect: () => void,
    options?: UseIntersectionOptions
): (node: Element | null) => void {
    const observerRef = useRef<IntersectionObserver | null>(null)
    // Реф для хранения последней версии колбэка без пересоздания observer
    const onIntersectRef = useRef(onIntersect)

    // Обновляем реф с колбэком при его изменении
    useEffect(() => {
        onIntersectRef.current = onIntersect
    }, [onIntersect])

    // Callback ref, который будет применен к элементу
    const ref = useCallback(
        (node: Element | null) => {
            // Отключаем старый observer, если он есть или узел изменился
            if (observerRef.current) {
                observerRef.current.disconnect()
                observerRef.current = null
            }

            if (node) {
                // Создаем новый observer
                const observer = new IntersectionObserver(
                    (entries) => {
                        // Вызываем колбэк только если элемент пересекается
                        if (entries[0].isIntersecting) {
                            onIntersectRef.current() // Используем актуальный колбэк из рефа
                        }
                    },
                    options // Передаем опции (например, threshold)
                )
                observer.observe(node)
                observerRef.current = observer
            }
        },
        [options] // Пересоздаем ref только если изменились options
    )

    // Очистка observer при размонтировании компонента, использующего хук
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [])

    return ref
}
